import { Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  VideoClip,
  VideoClipData,
  VideoProject,
  VideoProjectData,
  VideoScene,
  VideoSceneData,
  VideoShot,
  VideoShotAsset,
  VideoShotData,
} from '@models/Sessions/Idea/VideoProject';
import cloneDeep from 'lodash/cloneDeep';
import compose from 'lodash/flowRight';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import uniqueId from 'lodash/uniqueId';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  startWith,
} from 'rxjs';

import { VideoAssetResourceComponent } from './video-asset-resource/video-asset-resource.component';

export interface VideoProjectFormControl {
  scenes: FormArray<FormGroup<VideoSceneFormControl>>;
}

export type VideoProjectFormValue = FormGroup<VideoProjectFormControl>['value'];

export const createControl = (
  videoProject: VideoProject,
): VideoProjectFormControl => {
  return {
    scenes: new FormArray<FormGroup<VideoSceneFormControl>>(
      videoProject.scenes.map(
        scene =>
          new FormGroup<VideoSceneFormControl>(createSceneControl(scene)),
      ),
    ),
  };
};

export const reCreateControl = (
  videoProject: VideoProjectFormValue,
): VideoProjectFormControl => {
  return {
    scenes: new FormArray<FormGroup<VideoSceneFormControl>>(
      videoProject.scenes.map(
        scene =>
          new FormGroup<VideoSceneFormControl>(reCreateSceneControl(scene)),
      ),
    ),
  };
};

interface HasChangesParams {
  previous?: FormGroup<VideoProjectFormControl>['value'];
  current?: FormGroup<VideoProjectFormControl>['value'];
}

export const hasChanges = ({
  previous,
  current,
}: HasChangesParams): boolean => {
  return hasScenesChanges({
    previous: previous.scenes,
    current: current.scenes,
  });
};

export const mapFormValueToData = (
  videoProject: VideoProjectFormValue,
): VideoProjectData => {
  return {
    scenes: videoProject.scenes?.map(mapSceneFormValueToData) ?? [],
  };
};

export interface VideoSceneFormControl {
  id?: FormControl<string | undefined>;
  description: FormControl<string>;
  shots: FormArray<FormGroup<VideoShotFormControl>>;
  $key: FormControl<string>;
}

export type VideoSceneFormValue = FormGroup<VideoSceneFormControl>['value'];

export const emptySceneControl = (): VideoSceneFormControl => {
  return {
    description: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    shots: new FormArray<FormGroup<VideoShotFormControl>>([]),
    $key: new FormControl(
      {
        value: uniqueId('scene'),
        disabled: true,
      },
      {
        nonNullable: true,
      },
    ),
  };
};

export const createSceneControl = (
  scene: VideoScene,
): VideoSceneFormControl => {
  return {
    id: new FormControl(scene._id, {
      nonNullable: true,
    }),
    description: new FormControl(scene.description, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    shots: new FormArray<FormGroup<VideoShotFormControl>>(
      scene.shots.map(
        shot => new FormGroup<VideoShotFormControl>(createShotControl(shot)),
      ),
    ),
    $key: new FormControl(
      {
        value: uniqueId('scene'),
        disabled: true,
      },
      {
        nonNullable: true,
      },
    ),
  };
};

export const reCreateSceneControl = (
  scene: VideoSceneFormValue,
): VideoSceneFormControl => {
  return {
    id: new FormControl(scene.id, {
      nonNullable: true,
    }),
    description: new FormControl(scene.description, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    shots: new FormArray<FormGroup<VideoShotFormControl>>(
      scene.shots.map(
        shot => new FormGroup<VideoShotFormControl>(reCreateShotControl(shot)),
      ),
    ),
    $key: new FormControl(
      {
        value: uniqueId('scene'),
        disabled: true,
      },
      {
        nonNullable: true,
      },
    ),
  };
};

interface HasSceneChangesParams {
  previous?: VideoSceneFormValue;
  current?: VideoSceneFormValue;
}

export const hasSceneChanges = ({
  previous,
  current,
}: HasSceneChangesParams) => {
  return (
    !isEqual(isObject(previous), isObject(current)) ||
    !isEqual(previous.id, current.id) ||
    !isEqual(previous.description, current.description) ||
    hasShotsChanges({ previous: previous.shots, current: current.shots })
  );
};

interface HasScenesChangesParams {
  previous?: FormArray<FormGroup<VideoSceneFormControl>>['value'];
  current?: FormArray<FormGroup<VideoSceneFormControl>>['value'];
}

export const hasScenesChanges = ({
  previous = [],
  current = [],
}: HasScenesChangesParams): boolean => {
  return (
    previous.length !== current.length ||
    previous.some((previous, index) =>
      hasSceneChanges({ previous, current: current.at(index) }),
    )
  );
};

export const mapSceneFormValueToData = (
  scene: VideoSceneFormValue,
): VideoSceneData => {
  return {
    id: scene.id,
    description: scene.description,
    shots: scene.shots.map(mapShotFormValueToData),
  };
};

export interface VideoClipFormControl {
  id: FormControl<string>;
  asset: FormControl<VideoShotAsset>;
  startAt: FormControl<number>;
  endAt: FormControl<number>;
  $key: FormControl<string>;
}

export type VideoClipFormValue = FormGroup<VideoClipFormControl>['value'];

export const cloneClipControl = (
  clip: FormGroup<VideoClipFormControl>,
): VideoClipFormControl => {
  return {
    id: new FormControl(clip.value.id, {
      nonNullable: true,
    }),
    asset: new FormControl<VideoShotAsset>(clip.value.asset),
    startAt: new FormControl(clip.value.startAt, {
      nonNullable: true,
    }),
    endAt: new FormControl(clip.value.endAt, {
      nonNullable: true,
    }),
    $key: new FormControl(
      {
        value: uniqueId('clip'),
        disabled: true,
      },
      {
        nonNullable: true,
      },
    ),
  };
};

export const createClipControl = (clip: VideoClip): VideoClipFormControl => {
  return {
    id: new FormControl(clip._id, {
      nonNullable: true,
    }),
    asset: new FormControl<VideoShotAsset>(clip.asset),
    startAt: new FormControl(clip.startAt, {
      nonNullable: true,
    }),
    endAt: new FormControl(clip.endAt, {
      nonNullable: true,
    }),
    $key: new FormControl(
      {
        value: uniqueId('clip'),
        disabled: true,
      },
      {
        nonNullable: true,
      },
    ),
  };
};

export const reCreateClipControl = (
  clip: VideoClipFormValue,
): VideoClipFormControl => {
  return {
    id: new FormControl(clip.id, {
      nonNullable: true,
    }),
    asset: new FormControl<VideoShotAsset>(clip.asset),
    startAt: new FormControl(clip.startAt, {
      nonNullable: true,
    }),
    endAt: new FormControl(clip.endAt, {
      nonNullable: true,
    }),
    $key: new FormControl(
      {
        value: uniqueId('clip'),
        disabled: true,
      },
      {
        nonNullable: true,
      },
    ),
  };
};

interface HasClipChangesParams {
  previous?: FormGroup<VideoClipFormControl>['value'];
  current?: FormGroup<VideoClipFormControl>['value'];
}

export const hasClipChanges = ({ previous, current }: HasClipChangesParams) => {
  return (
    !isEqual(isObject(previous), isObject(current)) ||
    !isEqual(previous.id, current.id) ||
    !isEqual(previous.startAt, current.startAt) ||
    !isEqual(previous.endAt, current.endAt)
  );
};

interface HasClipsChangesParams {
  previous?: FormArray<FormGroup<VideoClipFormControl>>['value'];
  current?: FormArray<FormGroup<VideoClipFormControl>>['value'];
}

export const hasClipsChanges = ({
  previous = [],
  current = [],
}: HasClipsChangesParams): boolean => {
  return (
    previous.length !== current.length ||
    previous.some((previous, index) =>
      hasClipChanges({ previous, current: current.at(index) }),
    )
  );
};

export const mapClipFormValueToData = (
  shot: VideoClipFormValue,
): VideoClipData => {
  return {
    id: shot.id,
    startAt: shot.startAt,
    endAt: shot.endAt,
  };
};

export interface VideoShotFormControl {
  id?: FormControl<string>;
  parentId?: FormControl<string>;
  description: FormControl<string>;
  naturalLanguagePrompt: FormControl<string>;
  jsonPrompt: FormControl<Record<string, any>>;
  selectedClipIndex: FormControl<number>;
  clips: FormArray<FormGroup<VideoClipFormControl>>;
  $key: FormControl<string>;
}

export type VideoShotFormValue = FormGroup<VideoShotFormControl>['value'];

export const emptyShotControl = (): VideoShotFormControl => {
  return {
    description: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    naturalLanguagePrompt: new FormControl('', { nonNullable: true }),
    jsonPrompt: new FormControl(null),
    selectedClipIndex: new FormControl(-1),
    clips: new FormArray([]),
    $key: new FormControl(
      { value: uniqueId('shot'), disabled: true },
      { nonNullable: true },
    ),
  };
};

export const cloneShotControl = (
  shot: FormGroup<VideoShotFormControl>,
): VideoShotFormControl => {
  return {
    parentId: new FormControl(shot.value.id, {
      nonNullable: true,
    }),
    description: new FormControl(shot.value.description, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    naturalLanguagePrompt: new FormControl(
      shot.value.naturalLanguagePrompt ?? '',
      {
        nonNullable: true,
      },
    ),
    jsonPrompt: new FormControl(shot.value.jsonPrompt),
    selectedClipIndex: new FormControl(shot.value.selectedClipIndex, {
      nonNullable: true,
    }),
    clips: new FormArray<FormGroup<VideoClipFormControl>>(
      shot.controls.clips.controls.map(
        clip => new FormGroup(cloneClipControl(clip)),
      ),
    ),
    $key: new FormControl(
      {
        value: uniqueId('shot'),
        disabled: true,
      },
      {
        nonNullable: true,
      },
    ),
  };
};

export const createShotControl = (shot: VideoShot): VideoShotFormControl => {
  return {
    id: new FormControl(shot._id, {
      nonNullable: true,
    }),
    description: new FormControl(shot.description, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    naturalLanguagePrompt: new FormControl(shot.naturalLanguagePrompt ?? '', {
      nonNullable: true,
    }),
    jsonPrompt: new FormControl(shot.jsonPrompt),
    selectedClipIndex: new FormControl(shot.selectedClipIndex, {
      nonNullable: true,
    }),
    clips: new FormArray<FormGroup<VideoClipFormControl>>(
      shot.clips.map(
        asset => new FormGroup<VideoClipFormControl>(createClipControl(asset)),
      ),
    ),
    $key: new FormControl(
      {
        value: uniqueId('shot'),
        disabled: true,
      },
      {
        nonNullable: true,
      },
    ),
  };
};

export const reCreateShotControl = (
  shot: VideoShotFormValue,
): VideoShotFormControl => {
  return {
    id: new FormControl(shot.id, {
      nonNullable: true,
    }),
    description: new FormControl(shot.description, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    naturalLanguagePrompt: new FormControl(shot.naturalLanguagePrompt ?? '', {
      nonNullable: true,
    }),
    jsonPrompt: new FormControl(shot.jsonPrompt),
    selectedClipIndex: new FormControl(shot.selectedClipIndex, {
      nonNullable: true,
    }),
    clips: new FormArray<FormGroup<VideoClipFormControl>>(
      shot.clips.map(clip => new FormGroup(reCreateClipControl(clip))),
    ),
    $key: new FormControl(
      {
        value: uniqueId('shot'),
        disabled: true,
      },
      {
        nonNullable: true,
      },
    ),
  };
};

interface HasJsonPromptChangesParams {
  previous?: Record<string, any>;
  current?: Record<string, any>;
}

export const hasJsonPromptChanges = ({
  previous,
  current,
}: HasJsonPromptChangesParams) => {
  return (
    !isEqual(isObject(previous), isObject(current)) ||
    !isEqual(previous, current)
  );
};

interface HasShotChangesParams {
  previous?: FormGroup<VideoShotFormControl>['value'];
  current?: FormGroup<VideoShotFormControl>['value'];
}

export const hasShotChanges = ({ previous, current }: HasShotChangesParams) => {
  return (
    !isEqual(isObject(previous), isObject(current)) ||
    !isEqual(previous.id, current.id) ||
    !isEqual(previous.description, current.description) ||
    !isEqual(previous.naturalLanguagePrompt, current.naturalLanguagePrompt) ||
    hasJsonPromptChanges({
      previous: previous.jsonPrompt,
      current: current.jsonPrompt,
    }) ||
    !isEqual(previous.selectedClipIndex, current.selectedClipIndex) ||
    hasClipsChanges({ previous: previous.clips, current: current.clips })
  );
};

interface HasShotsChangesParams {
  previous?: FormArray<FormGroup<VideoShotFormControl>>['value'];
  current?: FormArray<FormGroup<VideoShotFormControl>>['value'];
}

export const hasShotsChanges = ({
  previous = [],
  current = [],
}: HasShotsChangesParams): boolean => {
  return (
    previous.length !== current.length ||
    previous.some((previous, index) =>
      hasShotChanges({ previous, current: current.at(index) }),
    )
  );
};

export const mapShotFormValueToData = (
  shot: VideoShotFormValue,
): VideoShotData => {
  return {
    id: shot.id,
    parentId: shot.parentId,
    description: shot.description,
    naturalLanguagePrompt: shot.naturalLanguagePrompt,
    jsonPrompt: shot.jsonPrompt,
    selectedClipIndex: shot.selectedClipIndex,
    clips: shot.clips.map(mapClipFormValueToData),
  };
};

@Injectable()
export class VideoProjectFormService {
  private activeResource = new BehaviorSubject<
    VideoAssetResourceComponent | undefined
  >(undefined);
  private initialValue = new BehaviorSubject<VideoProjectFormValue | undefined>(
    undefined,
  );

  setActiveResource(component: VideoAssetResourceComponent | undefined) {
    this.activeResource.next(component);
  }

  getActiveResource(): VideoAssetResourceComponent | undefined {
    return this.activeResource.getValue();
  }

  $onActiveResourceChanges(
    predicated?: (target: VideoAssetResourceComponent | undefined) => boolean,
  ) {
    if (predicated) {
      return this.activeResource.pipe(filter(predicated));
    }
    return this.activeResource.asObservable();
  }

  $onChanges(
    form: FormGroup<VideoProjectFormControl>,
  ): Observable<[boolean, VideoProjectFormValue | undefined]> {
    const initialValue$ = this.$getInitialValue();
    const valueChanges$ = form.valueChanges.pipe(startWith(form.value));

    return combineLatest([initialValue$, valueChanges$]).pipe(
      map(([previous, current]) => {
        return [
          previous ? hasChanges({ previous, current }) : false,
          current,
        ] satisfies [boolean, VideoProjectFormValue | undefined];
      }),
      distinctUntilChanged(),
    );
  }

  setInitialValue(initialValue: VideoProjectFormValue): this {
    this.initialValue.next(initialValue);
    return this;
  }

  getInitialValue() {
    return this.initialValue.getValue();
  }

  private $getInitialValue() {
    return this.initialValue.pipe(distinctUntilChanged());
  }

  $onSceneChanges(
    scene: FormGroup<VideoSceneFormControl>,
  ): Observable<[boolean, VideoSceneFormValue | undefined]> {
    const sceneInitialValue$ = this.$getSceneInitialValue(scene);
    const valueChanges$ = scene.valueChanges.pipe(startWith(scene.value));

    return combineLatest([sceneInitialValue$, valueChanges$]).pipe(
      map(([previous, current]) => {
        return [
          previous ? hasSceneChanges({ previous, current }) : false,
          current,
        ] satisfies [boolean, VideoSceneFormValue | undefined];
      }),
      distinctUntilChanged(),
    );
  }

  getSceneInitialValue(
    scene: FormGroup<VideoSceneFormControl>,
  ): VideoSceneFormValue | undefined {
    const initialValue = this.getInitialValue();
    return this.getSceneValue(scene)(initialValue);
  }

  private getSceneValue(scene: FormGroup<VideoSceneFormControl>) {
    return (
      project?: VideoProjectFormValue,
    ): VideoSceneFormValue | undefined => {
      return project?.scenes?.find?.(({ id }) => {
        return isEqual(id, scene.value.id);
      });
    };
  }

  private $getSceneInitialValue(scene: FormGroup<VideoSceneFormControl>) {
    return this.initialValue.pipe(
      map(value => {
        return this.getSceneValue(scene)(value);
      }),
      distinctUntilChanged(),
    );
  }

  private __scene__(shot: FormGroup<VideoShotFormControl>) {
    return shot.parent.parent as FormGroup<VideoSceneFormControl>;
  }

  setShotInitialValue(shot: FormGroup<VideoShotFormControl>): this {
    const _initialValue = cloneDeep(this.initialValue.getValue());
    if (!_initialValue) {
      return this;
    }
    _initialValue.scenes.forEach(_scene => {
      _scene.shots.forEach((_shot, index) => {
        if (isEqual(_shot.id, shot.value.id)) {
          _scene.shots[index] = shot.value;
        }
      });
    });
    this.initialValue.next(_initialValue);
    return this;
  }

  $onShotChanges(
    shot: FormGroup<VideoShotFormControl>,
  ): Observable<[boolean, VideoShotFormValue | undefined]> {
    const shotInitialValue$ = this.$getShotInitialValue(shot);
    const valueChanges$ = shot.valueChanges.pipe(startWith(shot.value));

    return combineLatest([shotInitialValue$, valueChanges$]).pipe(
      map(([previous, current]) => {
        return [
          previous ? hasShotChanges({ previous, current }) : false,
          current,
        ] satisfies [boolean, VideoShotFormValue | undefined];
      }),
    );
  }

  getShotInitialValue(
    shot: FormGroup<VideoShotFormControl>,
  ): VideoShotFormValue | undefined {
    const initialValue = this.getSceneInitialValue(this.__scene__(shot));
    return this.getShotValue(shot)(initialValue);
  }

  private getShotValue(shot: FormGroup<VideoShotFormControl>) {
    return (scene?: VideoSceneFormValue): VideoShotFormValue | undefined => {
      return scene?.shots?.find?.(({ id }) => {
        return isEqual(id, shot.value.id);
      });
    };
  }

  private $getShotInitialValue(shot: FormGroup<VideoShotFormControl>) {
    return this.initialValue.pipe(
      map(value => {
        return compose(
          this.getShotValue(shot),
          this.getSceneValue(this.__scene__(shot)),
        )(value);
      }),
      distinctUntilChanged(),
    );
  }

  private __shot__(clip: FormGroup<VideoClipFormControl>) {
    return clip.parent.parent as FormGroup<VideoShotFormControl>;
  }

  setClipInitialValue(clip: FormGroup<VideoClipFormControl>): this {
    const _initialValue = cloneDeep(this.initialValue.getValue());
    if (!_initialValue) {
      return this;
    }
    _initialValue.scenes.forEach(_scene => {
      _scene.shots.forEach(_shot => {
        _shot.clips.forEach((_clip, index) => {
          if (isEqual(_clip.id, clip.value.id)) {
            _shot.clips[index] = clip.value;
          }
        });
      });
    });
    this.initialValue.next(_initialValue);
    return this;
  }

  $onClipChanges(
    clip: FormGroup<VideoClipFormControl>,
  ): Observable<[boolean, VideoClipFormValue | undefined]> {
    const clipInitialValue$ = this.$getClipInitialValue(clip);
    const valueChanges$ = clip.valueChanges.pipe(startWith(clip.value));

    return combineLatest([clipInitialValue$, valueChanges$]).pipe(
      map(([previous, current]) => {
        return [
          previous ? hasClipChanges({ previous, current }) : false,
          current,
        ] satisfies [boolean, VideoClipFormValue | undefined];
      }),
    );
  }

  getClipInitialValue(
    clip: FormGroup<VideoClipFormControl>,
  ): VideoClipFormValue | undefined {
    const initialValue = this.getShotInitialValue(this.__shot__(clip));
    return this.getClipValue(clip)(initialValue);
  }

  private getClipValue(clip: FormGroup<VideoClipFormControl>) {
    return (shot?: VideoShotFormValue): VideoClipFormValue | undefined => {
      return shot?.clips?.find?.(({ id }) => {
        return isEqual(id, clip.value.id);
      });
    };
  }

  private $getClipInitialValue(clip: FormGroup<VideoClipFormControl>) {
    const shot = this.__shot__(clip);
    const scene = this.__scene__(shot);

    return this.initialValue.pipe(
      map(value => {
        return compose(
          this.getClipValue(clip),
          this.getShotValue(shot),
          this.getSceneValue(scene),
        )(value);
      }),
      distinctUntilChanged(),
    );
  }
}
